import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '../ui/Input';

const QuestionRow = ({ label, name, options, value, onChange, error }) => (
  <div className="py-6 border-b border-slate-50 last:border-0">
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
      <p className="text-slate-700 font-bold leading-relaxed max-w-xl text-base">{label}</p>
      <div className="flex flex-wrap gap-3">
        {options?.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(name, opt)}
            className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border-2 
              ${value === opt 
                ? 'bg-brand-600 border-brand-600 text-white shadow-premium ring-4 ring-brand-100' 
                : 'bg-white border-slate-100 text-slate-400 hover:border-brand-200 hover:text-brand-600 hover:bg-brand-50/30'}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
    {error && <p className="mt-3 text-xs text-red-500 flex items-center gap-2 font-bold bg-red-50 p-2 rounded-lg w-fit"><AlertCircle size={14}/> {error}</p>}
  </div>
);

export default function ScreeningAssessment({ section, answers, onChange, errors = {} }) {
  if (!section) return null;

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="bg-white/50 backdrop-blur-sm p-8 rounded-[2.5rem] border border-slate-50 shadow-soft">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-1.5 w-8 bg-brand-500 rounded-full"></div>
          <h3 className="text-xs font-black text-brand-600 uppercase tracking-[0.3em] font-outfit">{section.title}</h3>
        </div>
        
        <div className="space-y-2">
          {section.questions.map((q, qIndex) => (
            <div key={q.id}>
              {q.type === 'textarea' || q.type === 'text' ? (
                <div className="py-6">
                  <label className="block text-slate-700 font-bold mb-4 text-base">{qIndex + 1}. {q.label}</label>
                  {q.type === 'textarea' ? (
                    <textarea 
                      name={q.id} 
                      value={answers[q.id] || ''} 
                      onChange={(e) => onChange(q.id, e.target.value)}
                      className="w-full p-6 rounded-3xl border-2 border-slate-100 focus:border-brand-500 focus:ring-4 focus:ring-brand-50 outline-none min-h-[160px] transition-all text-slate-600 font-medium placeholder:text-slate-300" 
                      placeholder={q.placeholder || "Provide details..."}
                    />
                  ) : (
                    <Input 
                        name={q.id} 
                        value={answers[q.id] || ''} 
                        onChange={(e) => onChange(q.id, e.target.value)}
                        className="h-14 rounded-2xl bg-white border-slate-200 focus:border-brand-500"
                        placeholder="Provide details..." 
                    />
                  )}
                  {errors[q.id] && <p className="mt-3 text-xs text-red-500 flex items-center gap-2 font-bold bg-red-50 p-2 rounded-lg w-fit"><AlertCircle size={14}/> {errors[q.id]}</p>}
                </div>
              ) : (
                <QuestionRow 
                  label={`${qIndex + 1}. ${q.label}`} 
                  name={q.id} 
                  options={q.options} 
                  value={answers[q.id]} 
                  onChange={onChange} 
                  error={errors[q.id]} 
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
